import React from "react";
import { useSelector } from "react-redux";
import { selectors } from "rdx";

import { PrimaryButton } from "components/mui-styled-components";
import RightArrow2 from "components/Icons/Arrows/rightArrow2";

import { buildVisionProposalUrl, isBatteryOnlyVisionProposal } from "util/visionProposalUrl";
import { useGetProjectProposalsQuery } from "rdx/modules/projects/endpoints";
import { isBatteryProposal } from "components/Tabs/Project/ProposalTab/utils";
import useRouteMatcher from "hooks/useRouteMatcher";

import { theme } from "styles/themes";
import * as S from "./styles";

// A Battery-Only request never enters the design queue, so every other completion screen is
// wrong for it: the design-team, aurora and non-vision screens all promise a returned design
// and a turnaround the seller was never offered. This is the self-design screen without the
// array-design step.
const BatteryOnlyProposalDisplay = () => {
  const leadDetails = useSelector(selectors.selectLeadToProposalLeadDetails);
  const projectDetails = useSelector(selectors.selectLeadToProposalProjectDetails);
  const drawerProjectId = useSelector(selectors.selectLeadToProposalDrawerProjectId);
  const linkedProjectId = useSelector(selectors.selectLeadToProposalLinkedProjectId);
  const projectId = linkedProjectId ?? drawerProjectId;
  const { isEnterprise } = useRouteMatcher();
  const { data: proposalsData } = useGetProjectProposalsQuery(
    { id: projectId, enterprise: isEnterprise },
    { skip: !projectId },
  );

  const latestBatteryProposal = proposalsData?.proposals?.find(isBatteryProposal);
  const projectVisionProposal = isBatteryOnlyVisionProposal(projectDetails?.visionProposal)
    ? projectDetails?.visionProposal
    : undefined;
  const visionProposalUrl = buildVisionProposalUrl(
    latestBatteryProposal && "meta" in latestBatteryProposal ? latestBatteryProposal.meta : projectVisionProposal,
  );

  const openVisionInNewTab = () => {
    if (!visionProposalUrl) return;
    window.open(visionProposalUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <S.VisionRequestCompleteContainer>
      <S.VisionProposalCard>
        <S.VisionProposalCardHeader>Ready to build your proposal?</S.VisionProposalCardHeader>
        <S.VisionDescriptionContainer>
          <div>In Vision, you&apos;ll:</div>
          <S.VisionDescriptionRow>
            <RightArrow2 width="16" fill={theme.colors["dark-blue"]} /> Model energy usage
          </S.VisionDescriptionRow>
          <S.VisionDescriptionRow>
            <RightArrow2 width="18" fill={theme.colors["dark-blue"]} /> Pick battery equipment
          </S.VisionDescriptionRow>
          <S.VisionDescriptionRow>
            <RightArrow2 width="18" fill={theme.colors["dark-blue"]} /> Set up finance options
          </S.VisionDescriptionRow>
        </S.VisionDescriptionContainer>
        <PrimaryButton gradient disabled={!visionProposalUrl} onClick={openVisionInNewTab} width="100%" height="56px">
          <S.VisionButtonText>
            Continue to Vision <RightArrow2 width="22" fill={theme.colors.white} />
          </S.VisionButtonText>
        </PrimaryButton>
      </S.VisionProposalCard>
      <S.HomeownerDetailsContainer>
        <S.Divider />
        <S.DetailsRow>
          <S.HomeownerDetailsLabel>Homeowner</S.HomeownerDetailsLabel>
          <S.DetailsText>{leadDetails?.fullName}</S.DetailsText>
        </S.DetailsRow>
        <S.DetailsRow>
          <S.HomeownerDetailsLabel>Address</S.HomeownerDetailsLabel>
          <S.DetailsText>
            {leadDetails?.address?.street}, {leadDetails?.address?.city} {leadDetails?.address?.state}
          </S.DetailsText>
        </S.DetailsRow>
        <S.DetailsRow>
          <S.HomeownerDetailsLabel>Design</S.HomeownerDetailsLabel>
          <S.DetailsText>Battery (no design required)</S.DetailsText>
        </S.DetailsRow>
        <S.Divider />
      </S.HomeownerDetailsContainer>
    </S.VisionRequestCompleteContainer>
  );
};

export default BatteryOnlyProposalDisplay;
